import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import qs from 'qs'
import { NetworkError, RequestError } from './errors'
import { getRecaptchaToken } from 'utils/waitRecaptcha'
import { fileToBase64 } from 'utils/fileToBase64'
import { Storage } from 'utils/storage'
import type {
  AnyPage,
  AppointmentRequest,
  AppointmentResponse,
  AppointmentServiceRequest,
  Cashback,
  ConfirmPhonePurpose,
  FreeTimeSlot,
  GiftCardOrder,
  GiftProgram,
  NewsList,
  Profile,
  PromotionsList,
  QuickRegistrationRequest,
  RegistrationRequest,
  Salon,
  Service,
  SiteSettings,
  VisitCancelReason,
} from './api.types'

type Body = FormData | Record<string, unknown>

type ApiEnvelope = {
  error?: boolean,
  errorText?: string,
  errorCode?: string,
  data?: unknown,
}

const bearer = (token: string) => `Bearer ${token}`

class _APIService {

  readonly data = new _DataAPIService(this)

  readonly profile = new _ProfileAPIService(this)

  readonly auth = new _AuthAPIService(this)

  readonly certificates = new _CertificatesAPIService(this)

  readonly appointment = new _AppointmentAPIService(this)

  readonly axios: AxiosInstance

  private host = ''

  private tokenProvider: () => string | null = () => null

  constructor() {
    this.axios = axios.create({
      paramsSerializer: (params) => qs.stringify(params),
    })
    this.axios.interceptors.request.use((request) => {
      request.params ??= {}
      request.baseURL = this.host
      const token = this.tokenProvider()
      if (token && !request.headers.Authorization) {
        request.headers.Authorization = bearer(token)
      }
      return request
    })
    this.axios.interceptors.response.use((response) => {
      const result = response.data as ApiEnvelope
      if (result.error) {
        return Promise.reject(new RequestError(result.errorText ?? '', result.errorCode ?? ''))
      }
      return result.data as never
    }, (error: Error) => {
      return Promise.reject(new NetworkError(error.message))
    })
  }

  /** Where requests go: the backend URL on the server, the /api proxy in the browser */
  setHost(host: string) {
    this.host = host
  }

  /** The auth token is read from the store on every request */
  setTokenProvider(provider: () => string | null) {
    this.tokenProvider = provider
  }

  private request<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axios(path, config) as unknown as Promise<T>
  }

  private static parseBody(body: Body | undefined): AxiosRequestConfig {
    if (!body) {
      return {}
    }
    if (body instanceof FormData) {
      return {
        data: body,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    }
    return {
      data: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    }
  }

  get<T>(path: string, params: Record<string, unknown> = {}, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>(path, {
      method: 'GET',
      params,
      ...config,
    })
  }

  post<T>(path: string, body?: Body, config?: AxiosRequestConfig): Promise<T> {
    const { data, headers } = _APIService.parseBody(body)
    return this.request<T>(path, {
      method: 'POST',
      ...config,
      data,
      headers: {
        ...config?.headers,
        ...headers,
      },
    })
  }

  delete<T>(path: string, body?: Body, config?: AxiosRequestConfig): Promise<T> {
    const { data, headers } = _APIService.parseBody(body)
    return this.request<T>(path, {
      method: 'DELETE',
      ...config,
      data,
      headers: {
        ...config?.headers,
        ...headers,
      },
    })
  }

}

class _APIServiceConsumer {
  constructor(protected readonly api: _APIService) {
  }
}

class _DataAPIService extends _APIServiceConsumer {

  fetchSalons(): Promise<Salon[]> {
    return this.api.get('/salons')
  }

  fetchSettings(): Promise<SiteSettings> {
    return this.api.get('/settings')
  }

  fetchPage(path: string): Promise<AnyPage> {
    return this.api.get('/page-data', { path })
  }

  fetchServices(): Promise<Service[]> {
    return this.api.get('/services')
  }

  fetchNews(cityId: number | null, page = 0, perPage = 6): Promise<NewsList> {
    return this.api.get('/pages/news-page/', { page, perPage, cityId })
  }

  fetchPromotions(cityId: number | null, page = 0, perPage = 6): Promise<PromotionsList> {
    return this.api.get('/pages/promotion-page/', { page, perPage, cityId })
  }

}

class _ProfileAPIService extends _APIServiceConsumer {

  fetchProfile(): Promise<Profile> {
    return this.api.get('/profile')
  }

  editProfile(data: Record<string, unknown>): Promise<Profile> {
    return this.api.post('/profile', data)
  }

  fetchVisits(page = 0, perPage = 10): Promise<{ appointments: AppointmentResponse[], total: number }> {
    return this.api.get('/profile/appointments', { page, perPage })
  }

  fetchReviewableVisits(): Promise<AppointmentResponse[]> {
    return this.api.get('/profile/appointments/reviewable')
  }

  cancelVisit(id: number, reason: VisitCancelReason, comment: string): Promise<unknown> {
    return this.api.delete(`/profile/appointments/${id}`, { reason, comment })
  }

  async sendReview(
    id: number,
    answers: Record<string, number>,
    comment: string,
    photos: Blob[] = [],
  ): Promise<unknown> {
    return this.api.post('/profile/review', {
      id,
      answers,
      comment,
      photos: await Promise.all(photos.map(fileToBase64)),
    })
  }

  async sendAbstractReview(comment: string, photos: Blob[] = []): Promise<void> {
    await this.api.post('/profile/review', {
      comment,
      photos: await Promise.all(photos.map(fileToBase64)),
    })
  }

  fetchGifts(): Promise<GiftProgram[]> {
    return this.api.get('/profile/gifts')
  }

  fetchCashback(): Promise<Cashback> {
    return this.api.get('/profile/cashback')
  }

  /** A one-minute code the client shows at the desk to spend cashback */
  generateCashbackCode(): Promise<string> {
    return this.api.post('/profile/generate-cashback-code')
  }

}

class _AuthAPIService extends _APIServiceConsumer {

  signIn(credentials: { phone: string, password: string }): Promise<string> {
    return this.api.post('/auth/sign-in', credentials)
  }

  signUpCheck(data: Partial<RegistrationRequest>): Promise<unknown> {
    return this.api.post('/auth/sign-up/check', data)
  }

  signUp(data: RegistrationRequest): Promise<string> {
    return this.api.post('/auth/sign-up', data)
  }

  quickSignUp(data: QuickRegistrationRequest): Promise<string> {
    return this.api.post('/auth/quick-sign-up', data)
  }

  async confirmPhone(phone: string, purpose: ConfirmPhonePurpose, action = 'confirm'): Promise<{ expires_at: string }> {
    const token = await getRecaptchaToken(action)
    return this.api.post('/auth/confirm-phone', { phone, purpose, token })
  }

  refreshToken(): Promise<string> {
    return this.api.post('/auth/refresh')
  }

  changePassword(phone: string, password: string, code: string): Promise<unknown> {
    return this.api.post('/auth/change-password', { phone, password, code })
  }

  checkClient(phone: string): Promise<unknown> {
    return this.api.post('/auth/check-client', { phone })
  }

}

class _CertificatesAPIService extends _APIServiceConsumer {

  order(order: GiftCardOrder): Promise<{ formUrl?: string }> {
    return this.api.post('/gift-cards/order', order)
  }

  checkOrderStatus(orderId: string): Promise<unknown> {
    return this.api.get('/gift-cards/status', { orderId })
  }

}

const UTM_KEY = 'utm'

class _AppointmentAPIService extends _APIServiceConsumer {

  /** Free start times for the selected services; an empty list when the request fails */
  async fetchFreeTime(
    salonId: number,
    services: Record<number, AppointmentServiceRequest[]>,
    date: string,
  ): Promise<FreeTimeSlot[]> {
    try {
      return await this.api.post<FreeTimeSlot[]>('/appointment/free-time', { salon: salonId, services, date })
    } catch {
      return []
    }
  }

  /** Creates an appointment; a quick sign-up token authorizes a client who is not logged in */
  async create(data: AppointmentRequest, quickSignUpToken?: string | null): Promise<unknown> {
    const config: AxiosRequestConfig = {}
    if (quickSignUpToken) {
      config.headers = { Authorization: bearer(quickSignUpToken) }
    }
    const utm = Storage.getItem(UTM_KEY)
    const result = await this.api.post('/appointment', { ...data, utm }, config)
    Storage.deleteItem(UTM_KEY)
    return result
  }

  async update(id: number, data: AppointmentRequest): Promise<void> {
    await this.api.post(`/appointment/${id}`, data)
  }

}

export const APIService = new _APIService()
